import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { publicApi } from "./orderApi";

const KEY = (slug, bid) => `sc_online_cart_${slug}_${bid}`;
export const deviceId = () => {
  let id = localStorage.getItem("qr_device_id");
  if (!id) { id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).replace(/-/g, ""); localStorage.setItem("qr_device_id", id); }
  return id;
};
export const lineKey = (l) => `${l.item_id}|${l.variant_name || ""}|${[...(l.addon_ids || (l.addons || []).map((a) => a.id))].sort().join(",")}`;

// Local unit estimate from the public menu (same rule ItemSheet shows): used ONLY for the provisional amount while the server prices.
export const estimateUnit = (item, line) => {
  if (!item) return null;
  const v = item.variants?.find((x) => x.name === line.variant_name);
  const addonTotal = (line.addon_ids || []).reduce((s, id) => s + (item.addons?.find((a) => a.id === id)?.price || 0), 0);
  const listUnit = (v ? v.price : item.base_price) + addonTotal;
  const offerUnit = !v && item.offer_price != null ? item.offer_price : null;
  return offerUnit == null ? listUnit : item.offer_percent ? Math.round(listUnit * (1 - item.offer_percent / 100) * 100) / 100 : offerUnit + addonTotal;
};

/** Cart state. The server prices every change (prices, variants, extras, offers, taxes, availability stay authoritative);
 *  `estimate` gives an immediate provisional amount (last server unit prices → menu prices) so the UI never shows "…".
 *  Checkout is only possible with a confirmed server total (`priced` for the CURRENT lines). */
export const useCart = (slug, branchId, itemsById) => {
  const [lines, setLines] = useState([]);
  const [priced, setPriced] = useState(null);
  const [pricing, setPricing] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const unitMemo = useRef({});   // lineKey → server unit_total from the last successful pricing (per branch)
  const seqRef = useRef(0);
  useEffect(() => { unitMemo.current = {}; }, [slug, branchId]);
  const [coupon, setCoupon] = useState(() => (branchId && localStorage.getItem(`sc_online_coupon_${slug}_${branchId}`)) || "");

  useEffect(() => {
    if (!branchId) { setLines([]); return; }
    try { setLines(JSON.parse(localStorage.getItem(KEY(slug, branchId)) || "[]")); } catch { setLines([]); }
  }, [slug, branchId]);

  const persist = useCallback((next) => { setLines(next); if (branchId) localStorage.setItem(KEY(slug, branchId), JSON.stringify(next)); }, [slug, branchId]);

  const add = (line) => {
    const k = lineKey(line);
    const i = lines.findIndex((l) => lineKey(l) === k && (l.note || "") === (line.note || ""));
    if (i >= 0) { const next = [...lines]; next[i] = { ...next[i], qty: Math.min(20, next[i].qty + line.qty) }; persist(next); }
    else persist([...lines, line]);
  };
  const setQty = (idx, qty) => { const next = lines.map((l, i) => (i === idx ? { ...l, qty } : l)).filter((l) => l.qty > 0); persist(next); };
  const remove = (idx) => persist(lines.filter((_, i) => i !== idx));
  const clear = () => { persist([]); setCoupon(""); };
  const applyCoupon = (code) => { const c = (code || "").trim().toUpperCase(); setCoupon(c); if (branchId) localStorage.setItem(`sc_online_coupon_${slug}_${branchId}`, c); };
  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);

  useEffect(() => {
    if (!branchId || lines.length === 0) { setPriced(null); setPriceError(null); setPricing(false); return; }
    const seq = ++seqRef.current;          // only the answer to the LATEST cart may be applied (out-of-order / branch switch safe)
    const bid = branchId;
    setPricing(true);
    const t = setTimeout(() => {
      publicApi.post(`/${slug}/branches/${bid}/cart/price`, { lines: lines.map(({ item_id, variant_name, addon_ids, qty, note }) => ({ item_id, variant_name, addon_ids, qty, note })), coupon_code: coupon || null })
        .then((r) => {
          if (seq !== seqRef.current) return;
          for (const l of r.data.lines || []) unitMemo.current[lineKey(l)] = l.unit_total ?? l.unit_price;
          setPriced(r.data); setPriceError(null);
        })
        .catch((e) => { if (seq === seqRef.current) setPriceError(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not price the cart"); })
        .finally(() => { if (seq === seqRef.current) setPricing(false); });
    }, 150);
    return () => { clearTimeout(t); };
  }, [lines, slug, branchId, coupon]);

  // Provisional amounts for the current lines: exact per-line when the server already priced that line/variant/extras, else menu-based.
  const estimate = useMemo(() => {
    if (lines.length === 0) return null;
    let subtotal = 0, exact = true;
    for (const l of lines) {
      const k = lineKey(l);
      let unit = unitMemo.current[k];
      if (unit == null) { unit = estimateUnit(itemsById?.[l.item_id], l); exact = false; }
      if (unit == null) return null;
      subtotal += unit * l.qty;
    }
    subtotal = Math.round(subtotal * 100) / 100;
    const sameAsPriced = priced && priced.lines?.length === lines.length && lines.every((l, i) => lineKey(l) === lineKey(priced.lines[i]) && l.qty === priced.lines[i].qty);
    if (sameAsPriced) return { subtotal: priced.subtotal, total: priced.total, exact: true, confirmed: true };
    const extras = priced ? (priced.tax_total || 0) - (priced.discount_total || 0) : 0;   // last known taxes/discounts carried over as an approximation
    return { subtotal, total: Math.max(0, Math.round((subtotal + extras) * 100) / 100), exact: exact && !extras, confirmed: false };
  }, [lines, priced, itemsById]);
  const confirmed = !!priced && !pricing && !!estimate?.confirmed;   // server total matches the current lines → checkout allowed

  return { lines, add, setQty, remove, clear, count, priced, pricing, priceError, coupon, applyCoupon, estimate, confirmed };
};
